import { getCurrentYear } from '../utils';
import {
    ENERGY,
    SECURITY,
} from '../utils/ProductTypes';

export const AUCTION_TEMPLATE_NAME_LENGTH = 50;
export const auctionTemplateNameValidator = (t) => (input) => {
    let err = null;
    const maxLetters = AUCTION_TEMPLATE_NAME_LENGTH;
    if (input.trim().length === 0) {
        err = t('auction-templates.validation.auction-template-name-empty');
    } else if (input.length > maxLetters) {
        err = t('auction-templates.validation.auction-template-name-too-long', { maxLetters });
    }
    return err;
};

export const auctionTemplateDeliveryYearValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input && !input.match(/^\d+$/)) {
        err = t('auction-templates.validation.auction-template-delivery-year-not-a-number');
    } else if (parsedInput < getCurrentYear()) {
        err = t('auction-templates.validation.auction-template-delivery-year-in-past');
    }
    return err;
};

export const auctionTemplateQuantityValueValidator = (t, productType) => (input) => {
    let err = null;
    if (productType.is(ENERGY)) {
        err = auctionTemplateNumberOfLotsValidator(t)(input);
    } else if (productType.is(SECURITY)) {
        err = auctionTemplateQuantityValidator(t)(input);
    }
    return err;
};

export const auctionTemplateNumberOfLotsValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input && (!input.match(/^\d+$/) || parsedInput < 1)) {
        err = t('auction-templates.validation.auction-template-number-of-lots-not-positive-number');
    }
    return err;
};

export const auctionTemplateQuantityValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input && (!input.match(/^\d+$/) || parsedInput < 1)) {
        err = t('auction-templates.validation.auction-template-quantity-not-positive-number');
    }
    return err;
};

export const auctionTemplatePowerValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    if (input && (!input.match(/^\d+$/) || parsedInput < 1)) {
        err = t('auction-templates.validation.auction-template-power-not-positive-number');
    }
    return err;
};

export const auctionTemplateTimeValidator = (t) => (input) => {
    let err = null;
    if (input && !(input instanceof Date)) {
        err = t('auction-templates.validation.auction-template-time-not-a-date');
    }
    return err;
};

export const auctionTemplateSmallestQuantityUnitValidator = (t, minSmallestUnit, quantity, maxBidSize) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    const parsedQuantity = parseInt(quantity, 10);
    const parsedMaxBidSize = parseInt(maxBidSize, 10);
    let maxSmallestUnit = null;
    let valueGreaterThanSmallestErrMessage = null;
    if (parsedQuantity && parsedMaxBidSize) {
        if (parsedQuantity > parsedMaxBidSize) {
            maxSmallestUnit = parsedMaxBidSize;
            valueGreaterThanSmallestErrMessage = 'auction-template-smallest-quantity-unit-greater-than-max-bid-size';
        } else {
            maxSmallestUnit = parsedQuantity;
            valueGreaterThanSmallestErrMessage = 'auction-template-smallest-quantity-unit-greater-than-quantity';
        }
    } else if (parsedQuantity) {
        maxSmallestUnit = parsedQuantity;
        valueGreaterThanSmallestErrMessage = 'auction-template-smallest-quantity-unit-greater-than-quantity';
    } else if (parsedMaxBidSize) {
        maxSmallestUnit = parsedMaxBidSize;
        valueGreaterThanSmallestErrMessage = 'auction-template-smallest-quantity-unit-greater-than-max-bid-size';
    }
    if (parsedInput && !input.match(/^\d+$/)) {
        err = t('auction-templates.validation.auction-template-smallest-quantity-unit-invalid-integer');
    } else if (parsedInput < minSmallestUnit) {
        err = t('auction-templates.validation.auction-template-smallest-quantity-unit-lower-than-min', { minSmallestUnit });
    } else if (maxSmallestUnit && parsedInput > maxSmallestUnit) {
        err = t(`auction-templates.validation.${valueGreaterThanSmallestErrMessage}`);
    }
    return err;
};

export const auctionTemplateMaxBidSizeValidator = (t, staticMinMaxBidSize, smallestQuantityUnit, quantity) => (input) => {
    let err = null;
    const parsedInput = parseInt(input, 10);
    const parsedSmallestQuantityUnit = parseInt(smallestQuantityUnit, 10);
    const parsedQuantity = parseInt(quantity, 10);
    if (parsedInput && !input.match(/^\d+$/)) {
        err = t('auction-templates.validation.auction-template-maximum-bid-size-invalid-integer');
    } else if (parsedSmallestQuantityUnit && parsedInput < parsedSmallestQuantityUnit) {
        err = t('auction-templates.validation.auction-template-maximum-bid-size-lower-than-smallest-unit');
    } else if (!parsedSmallestQuantityUnit && parsedInput < staticMinMaxBidSize) {
        err = t('auction-templates.validation.auction-template-maximum-bid-size-lower-than-min', { minMaximumBidSize: staticMinMaxBidSize });
    } else if (parsedQuantity && parsedInput > parsedQuantity) {
        err = t('auction-templates.validation.auction-template-maximum-bid-size-greater-than-quantity');
    }
    return err;
};

const priceFormatValidator = (t) => (input) => {
    let err = null;
    const parsedInput = parseFloat(input);
    if (parsedInput && !input.match(/^-?\d+(\.\d{1,2})?$/)) {
        err = t('auction-templates.validation.auction-template-price-invalid-number');
    }
    return err;
};

export const auctionTemplateMinPriceValidator = (t, maxPrice) => (input) => {
    let err = priceFormatValidator(t)(input);
    const parsedInput = parseFloat(input);
    if (err === null && maxPrice !== null && parsedInput > maxPrice) {
        err = t('auction-templates.validation.auction-template-min-price-greater-than-max');
    }
    return err;
};

export const auctionTemplateMaxPriceValidator = (t, minPrice) => (input) => {
    let err = priceFormatValidator(t)(input);
    const parsedInput = parseFloat(input);
    if (err === null && minPrice !== null && parsedInput < minPrice) {
        err = t('auction-templates.validation.auction-template-max-price-lower-than-min');
    }
    return err;
};

export const AUCTION_TEMPLATE_DESCRIPTION_MAX_LENGTH = 255;
export const auctionTemplateDescriptionValidator = (t) => (input) => {
    let err = null;
    if (input.length > AUCTION_TEMPLATE_DESCRIPTION_MAX_LENGTH) {
        err = t('auction-templates.validation.auction-template-description-too-long', { maxLetters: AUCTION_TEMPLATE_DESCRIPTION_MAX_LENGTH });
    }
    return err;
};

export const auctionTemplateSpotReferencePriceValidator = (t, minSpotReferencePrice, maxSpotReferencePrice) => (input) => {
    let err = priceFormatValidator(t)(input);
    if (err === null) {
        const parsedInput = parseFloat(input);
        if (parsedInput < minSpotReferencePrice) {
            err = t('auction-templates.validation.auction-template-spot-reference-price-lower-than-min', { minSpotReferencePrice });
        } else if (parsedInput > maxSpotReferencePrice) {
            err = t('auction-templates.validation.auction-template-spot-reference-price-higher-than-max', { maxSpotReferencePrice });
        }
    }
    return err;
};

export const auctionTemplateAbsoluteDeviationValidator = (t, minAbsoluteDeviation, maxAbsoluteDeviation) => (input) => {
    let err = priceFormatValidator(t)(input);
    if (err === null) {
        const parsedInput = parseFloat(input);
        if (parsedInput < minAbsoluteDeviation) {
            err = t('auction-templates.validation.auction-template-absolute-deviation-lower-than-min', { minAbsoluteDeviation });
        } else if (parsedInput > maxAbsoluteDeviation) {
            err = t('auction-templates.validation.auction-template-absolute-deviation-higher-than-max', { maxAbsoluteDeviation });
        }
    }
    return err;
};

export const auctionTemplateRelativeDeviationValidator = (t, minRelativeDeviation, maxRelativeDeviation) => (input) => {
    let err = null;
    const parsedInput = parseFloat(input);
    if (parsedInput && !input.match(/^-?\d+(\.\d{1,2})?$/)) {
        err = t('auction-templates.validation.auction-template-relative-deviation-invalid-number');
    } else if (parsedInput < minRelativeDeviation) {
        err = t('auction-templates.validation.auction-template-relative-deviation-lower-than-min', { minRelativeDeviation });
    } else if (parsedInput > maxRelativeDeviation) {
        err = t('auction-templates.validation.auction-template-relative-deviation-higher-than-max', { maxRelativeDeviation });
    }
    return err;
};
